import * as React from "react"
import {
  type IntervalOrigin,
  type TransactionFilter,
} from "@digits-graphql/frontend/graphql-bearer"
import { svgPathStyles } from "@digits-shared/components/SVG/svgIconStyles"
import { SvgCurrencyDollar } from "@digits-shared/components/SVGIcons/line/CurrencyDollar.svg"
import { SvgMinus } from "@digits-shared/components/SVGIcons/line/Minus.svg"
import { AmountInput } from "@digits-shared/DesignSystem/FormElements/AmountInput"
import { MinMaxSlider } from "@digits-shared/DesignSystem/FormElements/MinMaxSlider"
import colors from "@digits-shared/themes/colors"
import { DetailText } from "@digits-shared/themes/typography"
import styled from "styled-components"
import {
  FilterAccordionContent,
  FilterAccordionHeader,
  FilterAccordionItem,
} from "src/shared/components/Filters/FilterAccordion"
import { useAmountFilter } from "src/shared/components/Filters/hooks/useAmountFilter"
import { FilterContentSection } from "src/shared/components/Filters/shared"
import { TransactionFilterType } from "src/shared/components/Filters/types"

const StyledFilterContentSection = styled(FilterContentSection)`
  display: flex;
  align-items: center;
  flex-direction: column;
  gap: 8px;
`

const DashIcon = styled(SvgMinus)`
  width: 24px;
  height: 24px;
  margin-bottom: 4px;
  ${svgPathStyles(colors.secondary50, 1.5)};
`

const InputLabel = styled(DetailText)`
  color: ${colors.secondary70};
`

const InputsContainer = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 8px;
`

const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 94px;
`

const SliderContainer = styled.div`
  width: 100%;
  margin-top: 8px;
`

interface FilterByAmountProps {
  minValue: number | undefined
  maxValue: number | undefined
  setMinValue: (value: number | undefined) => void
  setMaxValue: (value: number | undefined) => void
  filters: Omit<TransactionFilter, "viewKey">
  origin?: IntervalOrigin
  skip?: boolean
}

export const FilterByAmount: React.FC<FilterByAmountProps> = ({
  minValue,
  maxValue,
  setMinValue,
  setMaxValue,
  filters,
  origin,
  skip,
}) => {
  const { min, max, rangeMin, rangeMax, formattedMin, formattedMax, step } = useAmountFilter({
    filters,
    origin,
    skip,
  })

  // The inputs and sliders are controlled separately to prevent the slider from showing invalid values from the input
  // as the user is typing. The slider will sync with the inputs when the inputs are blurred. However, as the slider
  // moves, the input will update to show the current value of the slider.
  const [inputMin, setInputMin] = React.useState<number | undefined>(minValue)
  const [inputMax, setInputMax] = React.useState<number | undefined>(maxValue)
  const [sliderMin, setSliderMin] = React.useState<number | undefined>(minValue)
  const [sliderMax, setSliderMax] = React.useState<number | undefined>(maxValue)

  React.useEffect(() => {
    setInputMin(minValue)
    setSliderMin(minValue)
  }, [minValue])

  React.useEffect(() => {
    setInputMax(maxValue)
    setSliderMax(maxValue)
  }, [maxValue])

  const count = React.useMemo(() => (minValue ? 1 : 0) + (maxValue ? 1 : 0), [minValue, maxValue])

  const onMinChange = React.useCallback((number: number) => {
    setInputMin(number)
  }, [])

  const onMaxChange = React.useCallback((number: number) => {
    setInputMax(number)
  }, [])

  const onMinBlur = React.useCallback(
    (newMin: number | undefined) => {
      const absoluteMin = newMin === undefined ? undefined : Math.abs(newMin)

      let value: number | undefined
      if (absoluteMin === undefined) {
        value = undefined
      } else if (absoluteMin < min) {
        value = min
      } else if (absoluteMin > max || (maxValue !== undefined && absoluteMin > maxValue)) {
        value = undefined
      } else {
        value = absoluteMin
      }

      setInputMin(value)
      setMinValue(value)
    },
    [setMinValue, max, maxValue, min]
  )

  const onMaxBlur = React.useCallback(
    (newMax: number | undefined) => {
      const absoluteMax = newMax === undefined ? undefined : Math.abs(newMax)

      let value: number | undefined
      if (absoluteMax === undefined) {
        value = undefined
      } else if (absoluteMax > max) {
        value = max
      } else if (absoluteMax < min || (minValue !== undefined && absoluteMax < minValue)) {
        value = undefined
      } else {
        value = absoluteMax
      }

      setInputMax(value)
      setMaxValue(value)
    },
    [setMaxValue, max, min, minValue]
  )

  const onSliderChange = React.useCallback((value: number[]) => {
    if (value.length !== 2) return

    setSliderMin(value[0])
    setInputMin(value[0])
    setSliderMax(value[1])
    setInputMax(value[1])
  }, [])

  const onSliderCommit = React.useCallback(
    (value: number[]) => {
      if (value.length !== 2) return

      setMinValue(value[0])
      setMaxValue(value[1])
    },
    [setMinValue, setMaxValue]
  )

  return (
    <FilterAccordionItem value={TransactionFilterType.Amount}>
      <FilterAccordionHeader Icon={SvgCurrencyDollar} count={count}>
        Amount
      </FilterAccordionHeader>

      <FilterAccordionContent>
        <StyledFilterContentSection>
          <InputsContainer>
            <InputContainer>
              <InputLabel>Min</InputLabel>
              <AmountInput
                value={inputMin}
                onChange={onMinChange}
                onBlur={onMinBlur}
                placeholder={formattedMin}
                small
              />
            </InputContainer>

            <DashIcon />

            <InputContainer>
              <InputLabel>Max</InputLabel>
              <AmountInput
                value={inputMax}
                onChange={onMaxChange}
                onBlur={onMaxBlur}
                placeholder={formattedMax}
                small
              />
            </InputContainer>
          </InputsContainer>

          <SliderContainer>
            <MinMaxSlider
              trackColor={colors.secondary20}
              value={[
                sliderMin === undefined ? rangeMin : sliderMin,
                sliderMax === undefined ? rangeMax : sliderMax,
              ]}
              onValueChange={onSliderChange}
              onValueCommit={onSliderCommit}
              min={rangeMin}
              max={rangeMax}
              step={step}
            />
          </SliderContainer>
        </StyledFilterContentSection>
      </FilterAccordionContent>
    </FilterAccordionItem>
  )
}
